import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Clock, Users } from 'lucide-react';
import Header from '../../components/ui/Header';

const TrailDetailsKanjonTare = () => {
  // Trail basic information
  const trailInfo = {
    name: 'Kanjon Tare - priče o ljudima i rijeci',
    difficulty: 'Easy',
    length: '45.8 km',
    duration: '1-3 dana',
    theme: 'River Valleys',
    country: 'Montenegro'
  };

  // Trail description
  const description = `Treći tematski put vas vodi duž najdubljeg kanjona u Evropi, koji je svoje mjesto pronašao i na listi najvećih kanjona u svijetu. Tokom 50 kilometara, koliko ima od Mojkovca do Đurđevića Tare, a koje možete proći kolima, motorom ili biciklom, čekaju vas fantastični vidikovci, mistične šume, jezera, rijeka sa pritokama, a tu su i manastiri koji pričaju istoriju Potarja. Za kraj, čeka vas most na Đurđevića Tari, arhitektonsko čudo koje se izdiže 150 metara iznad rijeke, sagrađen davne 1937. godine. Uz stazu, nailazićete na informativne table koje pričaju posebnu priču o ljudima i rijeci, kao i smjerokaze za bezbjedno zaustavljanje u kanjonu kako biste napravili fantastične fotografije.`;

  // Natural attractions
  const naturalAttractions = [
  {
    id: 1,
    name: 'Rijeka Tara i njen kanjon',
    description: `Tara je najduža planinska rijeka u Crnoj Gori. Njeno ime potiče od ilirskog plemena Autarijati, koji su naseljavali područje kanjona tokom 4. i 5. vijeka. Nalazi se pod okriljem Nacionalnog parka Durmitor i Uneska, i s pravom nosi titulu ,,Suze Evrope", kao najveći rezervat pitke vode. Kanjon je okružen šumom crnog bora, slikovitim selima i manastirima. Glavna aktivnost na rijeci je rafting, koji se organizuje od maja do oktobra.`,
    gps: '43.12778250945779, 19.31181915890874',
    image: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763228206/Fotografija_3.1_-_kanjon_Tare_tnvcrk.jpg",
    imageAlt: 'Duboki kanjon rijeke Tare sa kristalno čistom vodom okružen visokim šumovitim liticama'
  },
  {
    id: 2,
    name: 'Bistrica i Ljutica – pritoke Tare',
    description: `Pritoke rijeke Tare, dinamične Bistrica i Ljutica, skrivene su u kanjonu. Njih je nemoguće uočiti sa magistralnog puta, ali postoje putokazi koji vode do njihovih obala. I jedna i druga su kratke, izbijaju iz stijena kanjona i strmoglavo se ulivaju u Taru. Upravo zbog fantastičnog ambijenta koji ih okružuje, vrijedi ih posjetiti.`,
    gps: 'Bistrica: 42.98931573843863, 19.411234524421904; Ljutica: 43.137820531518216, 19.301102951093014',
    image: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763228205/Fotografija_3.2_-_Bistrica_e0guex.jpg",
    imageAlt: 'Brzopotočne pritoke Bistrica i Ljutica koje se ulivaju u Taru kroz kanjon'
  },
  {
    id: 3,
    name: 'Kompleks crnog bora Crna poda',
    description: `Prašuma crnog bora – Crna poda, jedan je od najljepših šumskih predjela u Crnoj Gori. Ovaj kompleks borove šume se prostire na površini od 20 hektara, a prosječna starost borova je 400 godina. Pored borova kao najzastupljenijih drvenastih biljaka, svoje mjesto su ovdje našla i stabla smreke, jele i bukve, kao i 26 različitih biljnih staništa i više od 220 različitih biljaka.`,
    gps: '43.01016047058871, 19.413250070063846',
    image: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763228196/Fotografija_3.3_-_Crna_Poda_doaqnh.jpg",
    imageAlt: 'Prašuma Crna poda sa 400 godina starim crnim borovima na vertikalnim liticama'
  },
  {
    id: 4,
    name: 'Zabojsko jezero',
    description: `Zabojsko jezero je svoje mjesto pronašlo na 1.477 metara nadmorske visine, na Sinjajevini. Okruženo gustom četinarskom šumom, s pravom se smatra jednim od najljepših jezera u Crnoj Gori. Sa 19 metara dubine, treće je najdublje jezero u Crnoj Gori, poslije Crnog i Kapetanovog jezera, a zbog izuzetnih odlika uživa strogi režim zaštite unutar NP Durmitor.`,
    gps: '43.01132177378776, 19.37795294981456',
    image: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763228215/Fotografija_3.4_-_Zabojsko_jezero_bp6sfh.jpg",
    imageAlt: 'Zabojsko jezero okruženo gustom četinarskom šumom na planini Sinjajevini'
  }];


  // Cultural attractions
  const culturalAttractions = [
  {
    id: 1,
    name: 'Manastir Dobrilovina sa crkvom Svetog Đorđa',
    description: `Izgledom podsjeća na moračko manastirsko zdanje, pa je u narodu poznat i kao ,,Mala Morača". Kao nemanjićka zadužbina, pominje se u 16. vijeku, a obnovljen je 1609. godine. Hram je ponovo obnovljen i osveštan 1989. godine, a njegova atraktivna okolina dodatno privlači posjetioce da ga obiđu.`,
    gps: '43.02781620545061, 19.400831992989005',
    image: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763403978/16._Manastir_Dobrilovina_wjsoaw.jpg",
    imageAlt: 'Manastir Dobrilovina sa belim zidovima i crvenim krovom u zelenoj dolini'
  },
  {
    id: 2,
    name: 'Manastir Premćani sa crkvom Uspenja Presvete Bogorodice',
    description: `Nalazi se na desnoj obali Tare, u zaseoku Dovolja. Vjeruje se da ga je zidao kralj Milutin Nemanjić pri kraju 13. vijeka, dok se u istorijskim izvorima manastir Dovolja prvi put pominje 1513. godine. Smješten je visoko iznad kanjona Tare, pa se do ovog manastira može doći automobilom, asfaltnim putem od Pljevalja i Đurđevića Tare, ili planinarskom stazom.`,
    gps: '43.12352670047154, 19.343365512452344',
    image: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763228217/Fotografija_3.5_-_manastir_Prem%C4%87ani_wgjp6n.jpg",
    imageAlt: 'Manastir Premćani visoko iznad kanjona Tare na strmoj padini'
  },
  {
    id: 3,
    name: 'Manastir na Đurđevića Tari sa crkvom Svetog Arhanđela Mihaila',
    description: `Nalazi se u zaseoku Luke, oko kilometar nizvodno od velikog mosta na Tari. Manastir se nalazi na strmoj padini kanjona, dok se par stotina metara od manastira može prošetati do obale Tare. Tokom burne istorije, nekoliko puta je rušen i obnavljan, a bio je važan duhovni centar i u njemu je dugo radila prepisivačka škola.`,
    gps: '43.156851236649786, 19.285563626084333',
    image: "https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763228195/Fotografija_3.5_-_manastir_%C4%90ur%C4%91evi%C4%87_Tara_cxpydl.jpg",
    imageAlt: 'Manastir Đurđević Tara na strmoj padini kanjona blizu mosta'
  }];


  // Trail itinerary
  const itinerary = {
    title: 'KANJON TARE – PRIČE O LJUDIMA I RIJECI',
    description: `Vožnjom, obilazak tematskog puta započinje u Mojkovcu, na mostu kod Info centra TO Mojkovac, a završava kod mosta na Đurđevića Tari, nakon 45,8 km magistralnog puta (obilazak tematskog puta može da bude inverzni).`,
    stages: [
    {
      km: 21,
      location: 'Vidikovac',
      description: 'Interpretativna tabla tematskog puta. Mjesto za uživanje u panoramskom pogledu na početak kanjona rijeke Tare i na okolna planinska sela. Lokacija za fotografisanje.'
    },
    {
      km: 1,
      location: 'Crna poda',
      description: 'Prašuma crnog bora i strogi rezervat prirode. Uđite u prašumu da biste vidjeli i osjetili stabla stara više od 400 godina.'
    },
    {
      km: 4.9,
      location: 'Dobrilovina',
      description: 'Selo Dobrilovina, gdje je interpretativna tabla tematskog puta.'
    },
    {
      km: 6,
      location: 'Česma hladne vode',
      description: 'Označeno mjesto sa česmom hladne izvorske vode. Stanite da se okrijepite.'
    },
    {
      km: 4,
      location: 'Fotografska lokacija',
      description: 'Označeno mjesto za pravljenje lijepih snimaka kanjona.'
    },
    {
      km: 2.1,
      location: 'Most u Premćanima',
      description: 'Interpretativna tabla tematskog puta.'
    },
    {
      km: 5.1,
      location: 'Splavište za rafting',
      description: 'Interpretativna tabla tematskog puta, posvećena splavarima i triftarima, prvim krotiteljima nepokorne rijeke Tare.'
    },
    {
      km: 1.8,
      location: 'Ljutica',
      description: 'Najkraća evropska rijeka, Ljutica, gdje je interpretativna tabla tematske staze. Otkrijte istorijsku priču o herojima ovog kraja.'
    },
    {
      km: 1.3,
      location: 'Most na Đurđevića Tari',
      description: 'Krajnja ili početna tačka tematskog puta.'
    }]

  };

  // Alternative routes
  const alternativeRoutes = [
  {
    title: 'OTKRIJTE SINJAJEVINU VIA DINARICA STAZOM',
    subtitle: 'Ruta 1: Mojkovac – crkva Ružica – katun Okrugljak – katun Zaboj',
    description: 'Za pješake: Staza Mojkovac – crkva Ružica (katun Okrugljak), 19,7 km odnosno 6:30h. Staza crkva Ružica – katun Zaboj, 12 km odnosno 4h.',
    highlights: ['Seoska crkva Sv. Vasilija Ostroškog (Ružica)', 'Katun Okrugljak', 'Tradicionalni doručak u katunu']
  },
  {
    title: 'Ruta 2: Mojkovac – Zabojsko jezero – Kanjon Tare ili Zminičko jezero',
    description: 'Vožnja magistralnim putem od Mojkovca do Dobrilovine, 27 km. Obilazak Zabojskog jezera i katuna Zaboj sa tradicionalnim doručkom.',
    details: 'Dužina staze 8,7 km, vožnja terenskim vozilom 45 min ili pješačenje 3h i 686 m visinske razlike.'
  }];


  // Service providers data
  const serviceProviders = [
  {
    id: 1,
    name: 'Kamp Kljajevića luka',
    type: 'Accommodation & Activities',
    location: 'Đurđevića Tara, Pljevlja',
    phone: '+382 69 605 559',
    email: 'kljajevicaluka-rafting@t-com.me',
    website: 'www.tara-rafting.info',
    gps: '43.14909763824148, 19.299721754387903',
    description: 'Eko kamp na najljepšem mjestu Đurđevića Tare. Na pet hektara goste očekuju sportski tereni, bungalovi, ljetnjikovci, prostor za kampovanje, restoran. Aktivnosti: rafting, zip line, džip ture, kajaking.',
    services: ['Smještaj', 'Rafting', 'Zip line', 'Restoran', 'Kampovanje']
  },
  {
    id: 2,
    name: 'Konoba Suza Evrope',
    type: 'Restaurant & Activities',
    location: 'Đurđevića Tara, Pljevlja',
    phone: '+382 67 310 395',
    email: 'ostojiccompany@t-com.me',
    gps: '43.16255314746395, 19.290644461152603',
    description: 'Specijaliteti nacionalne kuhinje, sa akcentom na pripremu jela sa roštilja. Bašta restorana pruža fantastičan pogled na kanjon. Centar za posjetioce za organizaciju aktivnosti.',
    services: ['Restoran', 'Rafting', 'Zip line', 'Kanjoning']
  },
  {
    id: 3,
    name: 'Domaćinstvo Macanović',
    type: 'Organic Farm',
    location: 'selo Glibaći, Pljevlja',
    phone: '+382 69 023 819',
    email: 'zeljko.macanovic@yahoo.com',
    gps: '43.22754071098369, 19.25163970583612',
    description: 'Jedan od najvećih proizvođača organskih proizvoda u pljevaljskoj opštini. Uzgaja i prerađuje heljdu, speltu, raž, ječam, pšenicu i ovas. Ima svoj mlin.',
    services: ['Organska hrana', 'Brašno', 'Žitarice', 'Mlin']
  },
  {
    id: 4,
    name: 'Domaćinstvo Petrović',
    type: 'Farm Stay',
    location: 'selo Kosanica, Pljevlja',
    phone: '+382 67 873 694',
    email: 'petrovicveljko5@gmail.com',
    gps: '43.19585724333171, 19.319470656005624',
    description: 'Tradicionalno stočarstvo na 1.300 metara nadmorske visine. 37 hektara zemljišta, 23 muzne krave, 60 ovaca i 55 jagnjadi. Organski proizvođači žitarica.',
    services: ['Pljevaljski sir', 'Žitarice', 'Jagnjeće meso', 'Teleće meso']
  },
  {
    id: 5,
    name: 'Domaćinstvo Medojević',
    type: 'Beekeeping & Crafts',
    location: 'Kaludra, Mojkovac',
    phone: '+382 67 507 085',
    email: 'medojevic.ivana@gmail.com',
    gps: '43.000696854086414, 19.45709871916862',
    description: 'Pčelarstvo, uzgoj voća i drvodjeljstvo. Pravi rakije, džemove, sirupe i sokove. Ručno rađeni predmeti, namještaj, suveniri, gusle.',
    services: ['Med', 'Rakije', 'Džemovi', 'Drvodjeljstvo', 'Suveniri']
  },
  {
    id: 6,
    name: 'Domaćinstvo Rovčanin',
    type: 'Mountain Lodge',
    location: 'katun Zaboj, Mojkovac',
    phone: '+382 67 802 873',
    gps: '43.01305184476906, 19.37082080322521',
    description: 'Na 300 m od Zabojskog jezera. Kombinacija pčelarstva, poljoprivrede i ugošćavanja turista. Smještaj, restoran, proizvodi iz porodične radinosti.',
    services: ['Smještaj', 'Restoran', 'Degustacija', 'Via Dinarica']
  },
  {
    id: 7,
    name: 'Hotel Ravnjak',
    type: 'Hotel & Activities',
    location: 'Bistrica bb, Mojkovac',
    phone: '+382 67 557 466',
    email: 'info@ravnjak-hotel.com',
    gps: '42.98967422718784, 19.41159124033144',
    description: 'Smješten u kanjonu Tare, 15 km od Mojkovca. Specijaliteti: mojkovački stek, kačamak, cicvara, heljdija. Organizacija aktivnosti.',
    services: ['Smještaj', 'Restoran', 'Rafting', 'Paraglajding', 'Jahanje konja']
  },
  {
    id: 8,
    name: 'Kamp Šćepanović',
    type: 'Camp',
    location: 'Donja Polja, Mojkovac',
    phone: '+382 68 041 891',
    email: 'kampovanje@gmail.com',
    gps: '42.97185006761835, 19.53210586816673',
    description: 'Smještaj u 9 bungalova i dva apartmana. Posebna kuhinja za kampere. Vanjski toalet i tuš.',
    services: ['Kampovanje', 'Bungalovi', 'Apartmani', 'Prevoz']
  }];


  return (
    <>
      <Helmet>
        <title>Kanjon Tare - priče o ljudima i rijeci - Creative Twinning</title>
        <meta
          name="description"
          content="Otkrijte najdublji kanjon u Evropi kroz tematski put koji vas vodi od Mojkovca do Đurđevića Tare. Istražite prašume, manastire, jezera i čujte priče o ljudima i rijeci Tari." />

        <meta name="keywords" content="kanjon Tare, Durmitor, rafting, manastiri, Zabojsko jezero, Crna poda, Montenegro" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            {/* Back Navigation */}
            <div className="mb-8">
              <Link
                to="/thematic-trails-overview"
                className="inline-flex items-center text-primary hover:text-primary-dark transition-colors duration-200">

                <ArrowLeft className="w-4 h-4 mr-2" />
                Nazad na pregled staza
              </Link>
            </div>

            {/* Hero Section */}
            <div className="mb-12">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="aspect-w-16 aspect-h-9 lg:aspect-h-6">
                  <img
                    src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763504204/Blog_Kanjon_rijeke_Tare-2_igr0kz.jpg"
                    alt="Duboki kanjon rijeke Tare sa kristalno čistom vodom okružen visokim šumovitim liticama"
                    className="w-full h-64 lg:h-96 object-cover" />

                </div>
                <div className="p-8">
                  <div className="flex items-center gap-2 mb-4">
                    <img
                      src="https://res.cloudinary.com/dzt5ejpvb/image/upload/v1763502563/crnogorska_zastava_z81wq6.jpg"
                      alt="Montenegrin flag"
                      className="w-6 h-4 object-cover rounded" />

                    <span className="text-sm text-gray-600">{trailInfo?.country}</span>
                  </div>
                  
                  <h1 className="text-4xl font-bold text-gray-900 mb-6">{trailInfo?.name}</h1>
                  
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-primary" />
                      <div>
                        <p className="text-sm text-gray-600">Težina</p>
                        <p className="font-semibold">{trailInfo?.difficulty}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-primary" />
                      <div>
                        <p className="text-sm text-gray-600">Dužina</p>
                        <p className="font-semibold">{trailInfo?.length}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-primary" />
                      <div>
                        <p className="text-sm text-gray-600">Trajanje</p>
                        <p className="font-semibold">{trailInfo?.duration}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-primary" />
                      <div>
                        <p className="text-sm text-gray-600">Tema</p>
                        <p className="font-semibold">{trailInfo?.theme}</p>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-lg text-gray-700 leading-relaxed">{description}</p>
                </div>
              </div>
            </div>

            {/* Natural Attractions */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Prirodne atrakcije</h2>
              <div className="grid gap-8">
                {naturalAttractions?.map((attraction) =>
                <div key={attraction?.id} className="bg-white rounded-xl shadow-lg overflow-hidden">
                    <div className="lg:flex">
                      <div className="lg:w-1/3">
                        <img
                        src={attraction?.image}
                        alt={attraction?.imageAlt}
                        className="w-full h-64 lg:h-full object-cover" />

                      </div>
                      <div className="lg:w-2/3 p-8">
                        <h3 className="text-2xl font-bold text-gray-900 mb-4">{attraction?.name}</h3>
                        <div className="flex items-center gap-2 mb-4">
                          <MapPin className="w-4 h-4 text-primary" />
                          <span className="text-sm text-gray-600">GPS: {attraction?.gps}</span>
                        </div>
                        <p className="text-gray-700 leading-relaxed">{attraction?.description}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Cultural Attractions */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Kulturno-istorijske atrakcije</h2>
              <div className="grid gap-8">
                {culturalAttractions?.map((attraction) =>
                <div key={attraction?.id} className="bg-white rounded-xl shadow-lg overflow-hidden">
                    <div className="lg:flex">
                      <div className="lg:w-1/3">
                        <img
                        src={attraction?.image}
                        alt={attraction?.imageAlt}
                        className="w-full h-64 lg:h-full object-cover" />

                      </div>
                      <div className="lg:w-2/3 p-8">
                        <h3 className="text-2xl font-bold text-gray-900 mb-4">{attraction?.name}</h3>
                        <div className="flex items-center gap-2 mb-4">
                          <MapPin className="w-4 h-4 text-primary" />
                          <span className="text-sm text-gray-600">GPS: {attraction?.gps}</span>
                        </div>
                        <p className="text-gray-700 leading-relaxed">{attraction?.description}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Trail Itinerary */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Predlog obilaska</h2>
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h3 className="text-2xl font-bold text-primary mb-4">{itinerary?.title}</h3>
                <p className="text-gray-700 mb-8">{itinerary?.description}</p>
                
                <div className="space-y-6">
                  {itinerary?.stages?.map((stage, index) =>
                  <div key={index} className="flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">
                          {index + 1}
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-semibold text-primary">+{stage?.km} km</span>
                          <h4 className="font-bold text-gray-900">{stage?.location}</h4>
                        </div>
                        <p className="text-gray-700">{stage?.description}</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-8 p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Napomene:</strong> Iskoristite pauzu za obrok i odmor kod pružalaca usluga predloženih na ovom tematskom putu; probajte obavezno lokalne specijalitete: pastrmku iz Tare, mlječne tradicionalne proizvode, med. Obilazak staze u kanjonu Tare je pogodan za osobe sa posebnim potrebama.
                  </p>
                </div>
              </div>
            </section>

            {/* Alternative Routes */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Alternativne rute</h2>
              <div className="grid lg:grid-cols-2 gap-6">
                {alternativeRoutes?.map((route, index) =>
                <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                    <h3 className="text-xl font-bold text-primary mb-2">{route?.title}</h3>
                    {route?.subtitle &&
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">{route?.subtitle}</h4>
                  }
                    <p className="text-gray-700 mb-4">{route?.description}</p>
                    {route?.details &&
                  <p className="text-sm text-gray-600 mb-4">{route?.details}</p>
                  }
                    {route?.highlights &&
                  <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                        {route?.highlights?.map((highlight, idx) =>
                    <li key={idx}>{highlight}</li>
                    )}
                      </ul>
                  }
                  </div>
                )}
              </div>
            </section>

            {/* Service Providers */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Gdje se okrijepiti, jesti ili prespavati?</h2>
              <div className="grid lg:grid-cols-2 gap-6">
                {serviceProviders?.map((provider) =>
                <div key={provider?.id} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{provider?.name}</h3>
                        <p className="text-primary font-medium">{provider?.type}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-2 mb-4 text-sm text-gray-600">
                      <p><strong>Lokacija:</strong> {provider?.location}</p>
                      <p><strong>Telefon:</strong> {provider?.phone}</p>
                      {provider?.email && <p><strong>Email:</strong> {provider?.email}</p>}
                      {provider?.website && <p><strong>Web:</strong> {provider?.website}</p>}
                      <p><strong>GPS:</strong> {provider?.gps}</p>
                    </div>
                    
                    <p className="text-gray-700 mb-4">{provider?.description}</p>
                    
                    <div className="flex flex-wrap gap-2">
                      {provider?.services?.map((service, idx) =>
                    <span
                      key={idx}
                      className="px-3 py-1 bg-primary/10 text-primary text-xs rounded-full">

                          {service}
                        </span>
                    )}
                    </div>
                  </div>
                )}
              </div>
            </section>
          </div>
        </main>
      </div>
    </>);

};

export default TrailDetailsKanjonTare;